import React, { useState, useEffect } from 'react';
import { Clock, Save, RotateCcw, AlertCircle, CheckCircle } from 'lucide-react';
import { workdayService } from '../../services/workdayService';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday', short: 'Mon' },
  { value: 1, label: 'Tuesday', short: 'Tue' },
  { value: 2, label: 'Wednesday', short: 'Wed' },
  { value: 3, label: 'Thursday', short: 'Thu' },
  { value: 4, label: 'Friday', short: 'Fri' },
  { value: 5, label: 'Saturday', short: 'Sat' },
  { value: 6, label: 'Sunday', short: 'Sun' }
];

const DEFAULT_DAY_SCHEDULE = {
  is_working_day: false,
  start_time: '09:00',
  end_time: '17:00',
  lunch_start_time: '12:00',
  lunch_end_time: '13:00'
};

const WorkdaySettingsManager = () => {
  const [schedule, setSchedule] = useState({
    monday: DEFAULT_DAY_SCHEDULE,
    tuesday: DEFAULT_DAY_SCHEDULE,
    wednesday: DEFAULT_DAY_SCHEDULE,
    thursday: DEFAULT_DAY_SCHEDULE,
    friday: DEFAULT_DAY_SCHEDULE,
    saturday: DEFAULT_DAY_SCHEDULE,
    sunday: DEFAULT_DAY_SCHEDULE,
    timezone: 'UTC'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadWorkdaySettings();
  }, []);

  const loadWorkdaySettings = async () => {
    try {
      setLoading(true);
      const response = await workdayService.getUserWeeklySchedule();
      setSchedule(response.data);
    } catch (error) {
      console.error('Failed to load workday settings:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load workday settings. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (dayName, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [dayName]: {
        ...prev[dayName],
        [field]: value
      }
    }));
    
    // Clear any existing messages
    setMessage({ type: '', text: '' });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await workdayService.updateUserWeeklySchedule(schedule);
      setMessage({
        type: 'success',
        text: 'Workday settings saved successfully!'
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to save workday settings:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save workday settings. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadWorkdaySettings();
    setMessage({ type: '', text: '' });
  };

  const applyTemplate = (template) => {
    let templateSchedule = {};
    
    switch (template) {
      case 'standard':
        // Standard 9-5 weekdays
        DAYS_OF_WEEK.forEach(day => {
          const dayName = day.label.toLowerCase();
          templateSchedule[dayName] = {
            is_working_day: day.value < 5, // Monday-Friday
            start_time: day.value < 5 ? '09:00' : null,
            end_time: day.value < 5 ? '17:00' : null,
            lunch_start_time: day.value < 5 ? '12:00' : null,
            lunch_end_time: day.value < 5 ? '13:00' : null
          };
        });
        break;
        
      case 'extended':
        // Extended hours 8-6 weekdays
        DAYS_OF_WEEK.forEach(day => {
          const dayName = day.label.toLowerCase();
          templateSchedule[dayName] = {
            is_working_day: day.value < 5,
            start_time: day.value < 5 ? '08:00' : null,
            end_time: day.value < 5 ? '18:00' : null,
            lunch_start_time: day.value < 5 ? '12:00' : null,
            lunch_end_time: day.value < 5 ? '13:00' : null
          };
        });
        break;
        
      case 'parttime':
        // Part-time Mon/Wed/Fri
        DAYS_OF_WEEK.forEach(day => {
          const dayName = day.label.toLowerCase();
          const isWorkingDay = [0, 2, 4].includes(day.value); // Mon, Wed, Fri
          templateSchedule[dayName] = {
            is_working_day: isWorkingDay,
            start_time: isWorkingDay ? '09:00' : null,
            end_time: isWorkingDay ? '17:00' : null,
            lunch_start_time: isWorkingDay ? '12:00' : null,
            lunch_end_time: isWorkingDay ? '13:00' : null
          };
        });
        break;
        
      default:
        return;
    }
    
    setSchedule(prev => ({
      ...prev,
      ...templateSchedule
    }));
    
    setMessage({ type: '', text: '' });
  };

  const renderDaySchedule = (day) => {
    const dayName = day.label.toLowerCase();
    const daySchedule = schedule[dayName];
    
    return (
      <div key={day.value} className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">{day.label}</h4>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={daySchedule.is_working_day}
              onChange={(e) => handleDayChange(dayName, 'is_working_day', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
            <span className="ml-3 text-sm text-gray-700">
              {daySchedule.is_working_day ? 'Working Day' : 'Day Off'}
            </span>
          </label>
        </div>
        
        {daySchedule.is_working_day && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="time"
                value={daySchedule.start_time || ''}
                onChange={(e) => handleDayChange(dayName, 'start_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="time"
                value={daySchedule.end_time || ''}
                onChange={(e) => handleDayChange(dayName, 'end_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lunch Start
              </label>
              <input
                type="time"
                value={daySchedule.lunch_start_time || ''}
                onChange={(e) => handleDayChange(dayName, 'lunch_start_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lunch End
              </label>
              <input
                type="time"
                value={daySchedule.lunch_end_time || ''}
                onChange={(e) => handleDayChange(dayName, 'lunch_end_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-gray-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Working Hours</h3>
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              onChange={(e) => applyTemplate(e.target.value)}
              value=""
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="">Apply Template...</option>
              <option value="standard">Standard (9-5 weekdays)</option>
              <option value="extended">Extended (8-6 weekdays)</option>
              <option value="parttime">Part-time (Mon/Wed/Fri)</option>
            </select>
            
            <button
              onClick={handleReset}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 flex items-center"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </button>
          </div>
        </div>
        
        <p className="mt-2 text-sm text-gray-600">
          Configure your working hours for each day of the week. The scheduler will only suggest meetings during your working hours.
        </p>
      </div>

      <div className="p-6">
        {message.text && (
          <div className={`mb-6 p-4 rounded-md flex items-center ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {message.text}
          </div>
        )}

        <div className="space-y-4 mb-6">
          {DAYS_OF_WEEK.map(renderDaySchedule)}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Timezone
          </label>
          <select
            value={schedule.timezone}
            onChange={(e) => setSchedule(prev => ({ ...prev, timezone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
          </select>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Working Hours'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkdaySettingsManager;